import React, { useEffect, useRef, useState } from 'react';
import { View, StyleSheet, Animated, Easing, Dimensions } from 'react-native';
import {
  Text,
  Card,
  useTheme,
  Button,
  IconButton,
  Chip,
} from 'react-native-paper';
import type { ProgressAdjustment } from './AdaptiveProgressBar';

const { width: screenWidth } = Dimensions.get('window');

export interface ProgressAdjustmentNotificationProps {
  adjustment: ProgressAdjustment;
  onAccept: () => void;
  onDismiss: () => void;
  onViewDetails?: () => void;
  autoHide?: boolean;
  autoHideDelay?: number;
  style?: any;
}

export function ProgressAdjustmentNotification({
  adjustment,
  onAccept,
  onDismiss,
  onViewDetails,
  autoHide = false,
  autoHideDelay = 5000,
  style,
}: ProgressAdjustmentNotificationProps) {
  const theme = useTheme();
  const [isVisible, setIsVisible] = useState(true);
  const [showDetails, setShowDetails] = useState(false);

  // Animation values
  const slideAnimation = useRef(new Animated.Value(-screenWidth)).current;
  const scaleAnimation = useRef(new Animated.Value(0.8)).current;
  const opacityAnimation = useRef(new Animated.Value(0)).current;
  const progressAnimation = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    // Entrance animation
    Animated.parallel([
      Animated.timing(slideAnimation, {
        toValue: 0,
        duration: 500,
        easing: Easing.out(Easing.back(1.2)),
        useNativeDriver: true,
      }),
      Animated.timing(scaleAnimation, {
        toValue: 1,
        duration: 400,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }),
      Animated.timing(opacityAnimation, {
        toValue: 1,
        duration: 300,
        useNativeDriver: true,
      }),
    ]).start();

    // Auto-hide timer
    if (autoHide) {
      const timer = setTimeout(() => {
        handleDismiss();
      }, autoHideDelay);

      // Progress bar animation for auto-hide
      Animated.timing(progressAnimation, {
        toValue: 1,
        duration: autoHideDelay,
        useNativeDriver: false,
      }).start();

      return () => clearTimeout(timer);
    }
  }, [autoHide, autoHideDelay]);

  const handleAccept = () => {
    // Exit animation before accepting
    Animated.parallel([
      Animated.timing(slideAnimation, {
        toValue: screenWidth,
        duration: 300,
        easing: Easing.in(Easing.cubic),
        useNativeDriver: true,
      }),
      Animated.timing(opacityAnimation, {
        toValue: 0,
        duration: 300,
        useNativeDriver: true,
      }),
    ]).start(() => {
      onAccept();
    });
  };

  const handleDismiss = () => {
    // Exit animation before dismissing
    Animated.parallel([
      Animated.timing(slideAnimation, {
        toValue: -screenWidth,
        duration: 300,
        easing: Easing.in(Easing.cubic),
        useNativeDriver: true,
      }),
      Animated.timing(opacityAnimation, {
        toValue: 0,
        duration: 300,
        useNativeDriver: true,
      }),
    ]).start(() => {
      setIsVisible(false);
      onDismiss();
    });
  };

  const getAdjustmentIcon = () => {
    switch (adjustment.type) {
      case 'difficulty_change':
        return adjustment.impact > 0 ? 'trending-up' : 'trending-down';
      case 'timeline_extension':
        return 'clock-outline';
      case 'skill_focus':
        return 'target';
      case 'content_adaptation':
        return 'book-open-variant';
      default:
        return 'brain';
    }
  };

  const getAdjustmentColor = () => {
    if (adjustment.impact > 0.1) return '#4CAF50'; // Positive impact - green
    if (adjustment.impact < -0.1) return '#F44336'; // Negative impact - red
    return '#FF9800'; // Neutral impact - orange
  };

  const getAdjustmentTypeLabel = () => {
    switch (adjustment.type) {
      case 'difficulty_change':
        return 'Difficulty Adjustment';
      case 'timeline_extension':
        return 'Timeline Update';
      case 'skill_focus':
        return 'Focus Shift';
      case 'content_adaptation':
        return 'Content Adaptation';
      default:
        return 'Progress Adjustment';
    }
  };

  const getImpactDescription = () => {
    const impactPercent = Math.abs(adjustment.impact * 100);
    if (adjustment.impact > 0.1) {
      return `Expected to accelerate progress by ${impactPercent.toFixed(0)}%`;
    } else if (adjustment.impact < -0.1) {
      return `May slow progress by ${impactPercent.toFixed(0)}% but improves quality`;
    } else {
      return 'Minor adjustment to optimize your learning path';
    }
  };

  const adjustmentColor = getAdjustmentColor();
  const confidenceLevel =
    adjustment.confidence >= 0.8
      ? 'High'
      : adjustment.confidence >= 0.6
        ? 'Medium'
        : 'Low';

  if (!isVisible) return null;

  return (
    <Animated.View
      style={[
        styles.container,
        style,
        {
          transform: [
            { translateX: slideAnimation },
            { scale: scaleAnimation },
          ],
          opacity: opacityAnimation,
        },
      ]}
    >
      <Card
        style={[
          styles.card,
          {
            borderLeftColor: adjustmentColor,
            borderLeftWidth: 4,
          },
        ]}
        mode='elevated'
      >
        {/* Auto-hide progress bar */}
        {autoHide && (
          <Animated.View
            style={[
              styles.autoHideProgress,
              {
                width: progressAnimation.interpolate({
                  inputRange: [0, 1],
                  outputRange: ['0%', '100%'],
                }),
                backgroundColor: adjustmentColor,
              },
            ]}
          />
        )}

        <Card.Content style={styles.content}>
          {/* Header */}
          <View style={styles.header}>
            <View style={styles.headerLeft}>
              <View
                style={[
                  styles.iconContainer,
                  { backgroundColor: adjustmentColor + '20' },
                ]}
              >
                <IconButton
                  icon={getAdjustmentIcon()}
                  size={20}
                  iconColor={adjustmentColor}
                  style={styles.icon}
                />
              </View>

              <View style={styles.headerText}>
                <Text variant='titleSmall' style={styles.title}>
                  {getAdjustmentTypeLabel()}
                </Text>
                <Text variant='bodySmall' style={styles.subtitle}>
                  AI-powered optimization
                </Text>
              </View>
            </View>

            <IconButton
              icon='close'
              size={18}
              onPress={handleDismiss}
              style={styles.closeButton}
            />
          </View>

          {/* Main Content */}
          <View style={styles.mainContent}>
            <Text variant='bodyMedium' style={styles.reason}>
              {adjustment.reason}
            </Text>

            <Text variant='bodySmall' style={styles.impact}>
              {getImpactDescription()}
            </Text>

            {/* Confidence and Details */}
            <View style={styles.metaInfo}>
              <Chip
                icon='brain'
                mode='outlined'
                compact
                style={[
                  styles.confidenceChip,
                  {
                    borderColor: adjustmentColor,
                  },
                ]}
                textStyle={[styles.confidenceText, { color: adjustmentColor }]}
              >
                {confidenceLevel} Confidence
              </Chip>

              {onViewDetails && (
                <Button
                  mode='text'
                  compact
                  onPress={() => setShowDetails(!showDetails)}
                  style={styles.detailsButton}
                >
                  {showDetails ? 'Hide' : 'Show'} Details
                </Button>
              )}
            </View>

            {/* AI Reasoning Details */}
            {showDetails && (
              <Animated.View
                style={[
                  styles.detailsSection,
                  {
                    backgroundColor: adjustmentColor + '10',
                    borderColor: adjustmentColor + '30',
                  },
                ]}
              >
                <Text variant='bodySmall' style={styles.detailsTitle}>
                  AI Reasoning:
                </Text>
                <Text variant='bodySmall' style={styles.aiReasoning}>
                  {adjustment.aiReasoning}
                </Text>

                <View style={styles.technicalDetails}>
                  <Text variant='bodySmall' style={styles.technicalLabel}>
                    Confidence: {Math.round(adjustment.confidence * 100)}%
                  </Text>
                  <Text variant='bodySmall' style={styles.technicalLabel}>
                    Impact: {adjustment.impact > 0 ? '+' : ''}
                    {Math.round(adjustment.impact * 100)}%
                  </Text>
                  <Text variant='bodySmall' style={styles.technicalLabel}>
                    Applied:{' '}
                    {new Date(adjustment.appliedAt).toLocaleTimeString()}
                  </Text>
                </View>
              </Animated.View>
            )}
          </View>

          {/* Action Buttons */}
          <View style={styles.actions}>
            <Button
              mode='outlined'
              onPress={handleDismiss}
              style={styles.actionButton}
              labelStyle={styles.dismissButtonText}
            >
              Dismiss
            </Button>

            <Button
              mode='contained'
              onPress={handleAccept}
              style={[
                styles.actionButton,
                { backgroundColor: adjustmentColor },
              ]}
              labelStyle={styles.acceptButtonText}
            >
              Accept
            </Button>
          </View>
        </Card.Content>
      </Card>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    top: 60,
    left: 16,
    right: 16,
    zIndex: 1000,
  },
  card: {
    elevation: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
  },
  autoHideProgress: {
    position: 'absolute',
    top: 0,
    left: 0,
    height: 3,
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    zIndex: 1,
  },
  content: {
    padding: 16,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  iconContainer: {
    borderRadius: 20,
    marginRight: 12,
  },
  icon: {
    margin: 0,
  },
  headerText: {
    flex: 1,
  },
  title: {
    fontWeight: '600',
    marginBottom: 2,
  },
  subtitle: {
    opacity: 0.7,
  },
  closeButton: {
    margin: 0,
  },
  mainContent: {
    marginBottom: 16,
  },
  reason: {
    fontWeight: '500',
    marginBottom: 8,
    lineHeight: 20,
  },
  impact: {
    opacity: 0.8,
    marginBottom: 12,
    lineHeight: 18,
  },
  metaInfo: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  confidenceChip: {
    backgroundColor: 'transparent',
  },
  confidenceText: {
    fontSize: 12,
  },
  detailsButton: {
    margin: 0,
  },
  detailsSection: {
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    marginTop: 8,
  },
  detailsTitle: {
    fontWeight: '600',
    marginBottom: 6,
    opacity: 0.9,
  },
  aiReasoning: {
    lineHeight: 16,
    marginBottom: 8,
    opacity: 0.8,
  },
  technicalDetails: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
  },
  technicalLabel: {
    fontSize: 10,
    opacity: 0.7,
    textTransform: 'uppercase',
  },
  actions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
  },
  actionButton: {
    flex: 1,
  },
  dismissButtonText: {
    fontSize: 14,
  },
  acceptButtonText: {
    fontSize: 14,
    color: '#FFFFFF',
  },
});
